import Typography from "@mui/material/Typography";
import { GroupField } from "core/field/FieldService";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeleteEvent, Event, EventGroup, MoveEvent } from "utils/history/Event";
import { ListInfo } from "utils/list/List";
import TableActionbar from "utils/list/TableActionbar";
import { asTitle } from "utils/Utils";
import FieldBaseRow, { FieldRowProps } from "./FieldBaseRow";
import FieldTable from "./FieldTable";

export type GroupFieldRowProps = FieldRowProps & {
	field: GroupField;
};

const GroupFieldRow: React.FC<GroupFieldRowProps> = (
	props: GroupFieldRowProps
) => {
	const { t } = useTranslation();

	const [listInfo, setListInfo] = useState<ListInfo<string>>(ListInfo.init());

	useEffect(() => {
		setListInfo(prevState =>
			prevState.withKeys(props.field.children.map(field => field.uuid))
		);
	}, [props.field.children]);

	function changeField(event: Event): void {
		event.applyContext("/children");
		props.onChange(event);
	}

	function selectFields(keys: string[], select: boolean): void {
		setListInfo(listInfo.markSelected(keys, select));
	}

	function handleMoveUp(): void {
		const events: Event[] = [];
		for (const [index, field] of props.field.children.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.push(
					new MoveEvent(
						"/children/" + index,
						"/children/" + (index - 1)
					)
				);
			}
		}
		const event = new EventGroup(events);
		props.onChange(event);
	}

	function handleMoveDown(): void {
		const events: Event[] = [];
		for (const [index, field] of props.field.children.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.unshift(
					new MoveEvent(
						"/children/" + index,
						"/children/" + (index + 1)
					)
				);
			}
		}
		const event = new EventGroup(events);
		props.onChange(event);
	}

	function handleDelete(): void {
		const events: Event[] = [];
		for (const [index, field] of props.field.children.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.unshift(new DeleteEvent("/children/" + index, field));
			}
		}
		const event = new EventGroup(events);
		props.onChange(event);
	}

	return (
		<FieldBaseRow {...props}>
			<Typography variant="subtitle2">
				{asTitle(t("elements"))}
			</Typography>
			<TableActionbar
				listInfo={listInfo}
				onMoveUp={handleMoveUp}
				onMoveDown={handleMoveDown}
				onDelete={handleDelete}
			/>
			<FieldTable
				elements={props.field.children}
				listInfo={listInfo}
				editable={props.editable}
				onSelect={selectFields}
				onChange={changeField}
				onInsert={changeField}
			/>
		</FieldBaseRow>
	);
};

export default GroupFieldRow;
